/**
 * Dexie-backed `ConversationStore` (Requirements 4.1, 4.3, 4.7, 4.8,
 * 4.9, 6.6, 8.7, 12.5, 13.1, 16.10).
 *
 * The adapter wraps a single Dexie database (`ai-companion-chat`) with
 * four tables — `meta`, `threads`, `queue`, and `diagnostics` — and
 * implements the public `ConversationStore` contract one-to-one. All
 * multi-row writes happen inside Dexie transactions so partial state
 * is never observable; this is what backs the import-atomicity and
 * clear-thread-locality properties (22 and 9).
 *
 * Identity / time concerns (`crypto.randomUUID`, `Date.now`) are
 * routed through the optional `DexieStoreDeps` injection seam so
 * property tests can drive the adapter with a deterministic clock and
 * UUID counter (Property 7). The public `openDexieStore` factory uses
 * the real implementations.
 *
 * On open failure (`db.open()` rejection — IndexedDB unavailable in
 * private browsing, embedded WebViews, etc.), `openDexieStore` returns
 * `null` so the boot sequence can fall back to `createInMemoryStore`
 * and surface the persistence-unavailable banner (Requirement 12.2).
 */

import Dexie, { type Table } from 'dexie';

import type { RouterLogEntry } from '../../domain/modelRouter/types';
import {
  defaultPreferences,
  seedDefaultProfile,
} from '../../domain/types';
import type {
  ConversationThread,
  ConversationThreadSummary,
  ExportBundle,
  ImportResult,
  Message,
  Preferences,
  ProfileContext,
} from '../../domain/types';

import type { ConversationStore, QueuedMessageRow } from './types';
import { validateExportBundle } from './validator';

const DB_NAME = 'ai-companion-chat';
const DB_VERSION = 1;

const META_KEYS = {
  profile: 'profile',
  preferences: 'preferences',
  companionName: 'companionName',
  apiKey: 'apiKey',
} as const;

/** Maximum number of diagnostics entries retained in the ring buffer. */
const DIAGNOSTICS_CAP = 200;

interface MetaRow {
  key: string;
  value: unknown;
}

/** Internal Dexie database wrapper. */
class CompanionDB extends Dexie {
  meta!: Table<MetaRow, string>;
  threads!: Table<ConversationThread, string>;
  queue!: Table<QueuedMessageRow, string>;
  diagnostics!: Table<RouterLogEntry, number>;

  constructor() {
    super(DB_NAME);
    this.version(DB_VERSION).stores({
      meta: 'key',
      threads: 'id, updatedAt',
      queue: 'id, enqueuedAt',
      diagnostics: 'seq',
    });
  }
}

/** Injection seam for the timestamp + uuid providers (testability). */
export interface DexieStoreDeps {
  nowMs: () => number;
  uuid: () => string;
}

const defaultDeps: DexieStoreDeps = {
  nowMs: () => Date.now(),
  uuid: () => crypto.randomUUID(),
};

function projectSummary(thread: ConversationThread): ConversationThreadSummary {
  return {
    id: thread.id,
    title: thread.title,
    updatedAt: thread.updatedAt,
    messageCount: thread.messages.length,
  };
}

/**
 * Construct a Dexie-backed `ConversationStore`. The `db` argument is
 * the already-opened Dexie database; the factory `openDexieStore` is
 * the typical entry point.
 */
export function createDexieStore(
  db: CompanionDB,
  deps: DexieStoreDeps = defaultDeps,
): ConversationStore {
  const getMetaValue = async <T>(key: string): Promise<T | null> => {
    const row = await db.meta.get(key);
    if (!row) return null;
    return (row.value as T | null) ?? null;
  };

  const setMetaValue = async (key: string, value: unknown): Promise<void> => {
    await db.meta.put({ key, value });
  };

  const requireThread = async (id: string): Promise<ConversationThread> => {
    const t = await db.threads.get(id);
    if (!t) {
      throw new Error(`Thread ${id} not found`);
    }
    return t;
  };

  return {
    // ---------- Threads ----------

    async listThreads(): Promise<ConversationThreadSummary[]> {
      const all = await db.threads.toArray();
      return all
        .slice()
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map(projectSummary);
    },

    async getThread(id: string): Promise<ConversationThread | null> {
      const t = await db.threads.get(id);
      return t ?? null;
    },

    async createThread(title: string): Promise<ConversationThread> {
      const now = deps.nowMs();
      const thread: ConversationThread = {
        id: deps.uuid(),
        title,
        createdAt: now,
        updatedAt: now,
        messages: [],
        rollingSummary: null,
        summarizedThroughMessageId: null,
      };
      await db.threads.put(thread);
      return thread;
    },

    async renameThread(id: string, title: string): Promise<void> {
      await db.transaction('rw', db.threads, async () => {
        const t = await requireThread(id);
        t.title = title;
        t.updatedAt = deps.nowMs();
        await db.threads.put(t);
      });
    },

    async deleteThread(id: string): Promise<void> {
      await db.threads.delete(id);
    },

    async clearThread(id: string): Promise<void> {
      await db.transaction('rw', db.threads, async () => {
        const t = await requireThread(id);
        t.messages = [];
        t.rollingSummary = null;
        t.summarizedThroughMessageId = null;
        t.updatedAt = deps.nowMs();
        await db.threads.put(t);
      });
    },

    async appendMessage(threadId: string, message: Message): Promise<void> {
      await db.transaction('rw', db.threads, async () => {
        const t = await requireThread(threadId);
        t.messages.push(message);
        t.updatedAt = deps.nowMs();
        await db.threads.put(t);
      });
    },

    async updateMessage(
      threadId: string,
      messageId: string,
      patch: Partial<Message>,
    ): Promise<void> {
      await db.transaction('rw', db.threads, async () => {
        const t = await requireThread(threadId);
        const idx = t.messages.findIndex((m) => m.id === messageId);
        if (idx === -1) {
          throw new Error(
            `Message ${messageId} not in thread ${threadId}`,
          );
        }
        const existing = t.messages[idx];
        if (existing === undefined) {
          throw new Error(
            `Message ${messageId} not in thread ${threadId}`,
          );
        }
        const merged: Message = {
          ...existing,
          ...patch,
          // id and threadId are immutable.
          id: existing.id,
          threadId: existing.threadId,
        };
        t.messages[idx] = merged;
        t.updatedAt = deps.nowMs();
        await db.threads.put(t);
      });
    },

    async setRollingSummary(
      threadId: string,
      summary: string,
      summarizedThroughMessageId: string,
    ): Promise<void> {
      await db.transaction('rw', db.threads, async () => {
        const t = await requireThread(threadId);
        t.rollingSummary = summary;
        t.summarizedThroughMessageId = summarizedThroughMessageId;
        let crossed = false;
        for (const m of t.messages) {
          if (!crossed) {
            m.summarized = true;
            if (m.id === summarizedThroughMessageId) {
              crossed = true;
            }
          }
        }
        t.updatedAt = deps.nowMs();
        await db.threads.put(t);
      });
    },

    // ---------- Profile / preferences / companion name ----------

    getProfile(): Promise<ProfileContext | null> {
      return getMetaValue<ProfileContext>(META_KEYS.profile);
    },

    async setProfile(p: ProfileContext): Promise<void> {
      await setMetaValue(META_KEYS.profile, p);
    },

    getPreferences(): Promise<Preferences | null> {
      return getMetaValue<Preferences>(META_KEYS.preferences);
    },

    async setPreferences(p: Preferences): Promise<void> {
      await setMetaValue(META_KEYS.preferences, p);
    },

    async getCompanionName(): Promise<string | null> {
      const row = await db.meta.get(META_KEYS.companionName);
      if (!row) return null;
      return (row.value as string | null) ?? null;
    },

    async setCompanionName(name: string | null): Promise<void> {
      await setMetaValue(META_KEYS.companionName, name);
    },

    // ---------- API key ----------

    async getApiKey(): Promise<string | null> {
      const row = await db.meta.get(META_KEYS.apiKey);
      if (!row) return null;
      return (row.value as string | null) ?? null;
    },

    async setApiKey(key: string | null): Promise<void> {
      await setMetaValue(META_KEYS.apiKey, key);
    },

    // ---------- Queue ----------

    async enqueueMessage(row: QueuedMessageRow): Promise<void> {
      await db.queue.put(row);
    },

    async listQueuedMessages(): Promise<QueuedMessageRow[]> {
      return db.queue.orderBy('enqueuedAt').toArray();
    },

    async removeQueuedMessage(id: string): Promise<void> {
      await db.queue.delete(id);
    },

    async updateQueuedMessage(
      id: string,
      patch: Partial<QueuedMessageRow>,
    ): Promise<void> {
      await db.transaction('rw', db.queue, async () => {
        const existing = await db.queue.get(id);
        if (!existing) {
          throw new Error(`Queued message ${id} not found`);
        }
        const merged: QueuedMessageRow = {
          ...existing,
          ...patch,
          id: existing.id,
        };
        await db.queue.put(merged);
      });
    },

    // ---------- Diagnostics ----------

    async appendLogEntry(entry: RouterLogEntry): Promise<void> {
      await db.transaction('rw', db.diagnostics, async () => {
        await db.diagnostics.put(entry);
        const total = await db.diagnostics.count();
        if (total > DIAGNOSTICS_CAP) {
          // Drop oldest by `seq` ASC until we're back at the cap.
          const overflow = total - DIAGNOSTICS_CAP;
          const oldest = await db.diagnostics
            .orderBy('seq')
            .limit(overflow)
            .toArray();
          for (const row of oldest) {
            await db.diagnostics.delete(row.seq);
          }
        }
      });
    },

    async listLogEntries(limit: number): Promise<RouterLogEntry[]> {
      const safeLimit = Math.max(0, Math.floor(limit));
      const newestDesc = await db.diagnostics
        .orderBy('seq')
        .reverse()
        .limit(safeLimit)
        .toArray();
      // Caller wants chronological order — reverse the desc slice.
      return newestDesc.reverse();
    },

    // ---------- Bulk ----------

    async exportAll(): Promise<ExportBundle> {
      const [profile, preferences, companionNameRow, allThreads] =
        await Promise.all([
          getMetaValue<ProfileContext>(META_KEYS.profile),
          getMetaValue<Preferences>(META_KEYS.preferences),
          db.meta.get(META_KEYS.companionName),
          db.threads.toArray(),
        ]);
      const companionName =
        companionNameRow === undefined
          ? null
          : ((companionNameRow.value as string | null) ?? null);
      return {
        schemaVersion: 1,
        exportedAt: deps.nowMs(),
        profile: profile ?? seedDefaultProfile(),
        preferences: preferences ?? defaultPreferences(),
        companionName,
        threads: allThreads
          .slice()
          .sort((a, b) => a.createdAt - b.createdAt),
      };
    },

    async importAll(bundle: unknown): Promise<ImportResult> {
      // Validate FIRST so we can reject without touching any table
      // (Property 22 — Import atomicity on malformed input).
      const result = validateExportBundle(bundle);
      if (!result.ok) {
        return { ok: false, errorPath: result.errorPath, reason: result.reason };
      }
      const valid = result.bundle;
      await db.transaction(
        'rw',
        [db.meta, db.threads],
        async () => {
          await db.meta.put({ key: META_KEYS.profile, value: valid.profile });
          await db.meta.put({
            key: META_KEYS.preferences,
            value: valid.preferences,
          });
          await db.meta.put({
            key: META_KEYS.companionName,
            value: valid.companionName,
          });
          await db.threads.clear();
          if (valid.threads.length > 0) {
            await db.threads.bulkPut(valid.threads);
          }
        },
      );
      return { ok: true, importedThreadCount: valid.threads.length };
    },

    async wipeAll(): Promise<void> {
      await db.transaction(
        'rw',
        [db.meta, db.threads, db.queue, db.diagnostics],
        async () => {
          await Promise.all([
            db.meta.clear(),
            db.threads.clear(),
            db.queue.clear(),
            db.diagnostics.clear(),
          ]);
          await db.meta.put({
            key: META_KEYS.profile,
            value: seedDefaultProfile(),
          });
          await db.meta.put({
            key: META_KEYS.preferences,
            value: defaultPreferences(),
          });
          await db.meta.put({ key: META_KEYS.companionName, value: null });
        },
      );
    },
  };
}

/**
 * Try to open the Dexie database and return a wired-up adapter.
 *
 * Returns `null` if `db.open()` rejects — the typical IndexedDB-blocked
 * failure mode (private browsing, embedded WebViews, disabled storage).
 * The caller falls back to `createInMemoryStore` and surfaces the
 * persistence-unavailable banner (Requirement 12.2).
 */
export async function openDexieStore(): Promise<ConversationStore | null> {
  try {
    const db = new CompanionDB();
    await db.open();
    return createDexieStore(db);
  } catch {
    // Swallow the error — the caller does not need details, only a
    // null return so it can swap in the in-memory adapter.
    return null;
  }
}
